/**
 * Admin CRUD schemas for sellers.
 * Source of truth: docs/architecture/database-schema.md §4.2.
 *
 * Story 3.6 extends the original Story 1.4 stub with:
 *   - `Seller` re-export (read-side projection from `@sdr-agend/schemas`)
 *   - `SellerCreate` / `SellerUpdate` (already partial in 1.4)
 *   - `SellerListQuery` / `SellerListResponse` / `SellerResponse`
 *
 * Backwards-compatible: existing imports of `SellerCreate`, `SellerUpdate`,
 * `Seller` remain identical in shape — we only add the list/response wrappers
 * needed by the admin UI.
 */
import { Type } from '@sinclair/typebox';
import { Phone, Seller } from '@sdr-agend/schemas';
export { Seller };
/**
 * Notification preferences — typed as a free-form `Record<string, boolean>`
 * to match the `notification_event` enum without coupling the admin schema
 * to its current values (architecture §6 may grow the enum). The frontend
 * renders a fixed checklist (see `SellerDetail.tsx`).
 */
const NotificationPrefs = Type.Record(Type.String(), Type.Boolean());
/**
 * Payload accepted by `POST /admin/sellers`.
 * `notificationPrefs` defaults to `{}` server-side; the field is optional on
 * input so the form can omit it.
 */
export const SellerCreate = Type.Object({
    name: Type.String({ minLength: 1, maxLength: 200 }),
    phone: Phone,
    email: Type.Optional(Type.String({ format: 'email' })),
    calendarId: Type.Optional(Type.String()),
    ghlUserId: Type.Optional(Type.String()),
    active: Type.Optional(Type.Boolean()),
    notificationPrefs: Type.Optional(NotificationPrefs),
});
/**
 * Payload accepted by `PATCH /admin/sellers/:id`.
 * All fields optional — partial update.
 */
export const SellerUpdate = Type.Object({
    name: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
    phone: Type.Optional(Phone),
    email: Type.Optional(Type.String({ format: 'email' })),
    calendarId: Type.Optional(Type.String()),
    ghlUserId: Type.Optional(Type.String()),
    active: Type.Optional(Type.Boolean()),
    notificationPrefs: Type.Optional(NotificationPrefs),
});
/**
 * `SellerListQuery` — query string of `GET /admin/sellers`.
 *
 * `active` is accepted as a string (`"true"` | `"false"`) because Fastify
 * does not coerce booleans in query strings. The route handler interprets it.
 */
export const SellerListQuery = Type.Object({
    active: Type.Optional(Type.String()),
    search: Type.Optional(Type.String()),
});
export const SellerListResponse = Type.Object({
    sellers: Type.Array(Seller),
});
export const SellerResponse = Type.Object({
    seller: Seller,
});
/**
 * Default notification toggles surfaced to the form. Keys mirror the
 * `notification_event` enum (database-schema.md §3) and the `SellerEvent`
 * union exported from `apps/api/src/services/notifications/seller-notifier.ts`
 * (Story 4.4) so the user can opt-in per event type. The full list is the
 * source of truth in the admin UI.
 *
 * Story 4.4 adds `reschedule_limit_reached` and `reschedule_no_slots`
 * (emitted by Story 4.5 CalendarService when a reschedule cannot be
 * fulfilled). `new_scheduled` is kept for backwards-compat with Story 3.6
 * even though `SellerEvent` does not list it — opting in still stores the
 * key in JSONB without breaking the notifier (unmapped events are skipped).
 */
export const SELLER_NOTIFICATION_EVENTS = [
    'confirmed',
    'cancelled',
    'rescheduled',
    'no_response',
    'reschedule_limit_reached',
    'reschedule_no_slots',
    'attendance_callback',
    'lead_qualified',
];
//# sourceMappingURL=sellers.js.map